import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { DateRangePicker } from '@m/alchemy-ui/DateRangePicker';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { BarChart } from '@m/alchemy-ui/esm/visualizations/bar-chart/BarChart';
import { subWeeks, toDate } from 'date-fns';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import { useUserFields } from '../../hooks';
import { useWindowWidth } from '../../hooks/breakpoint';
import { CommunityContext } from '../../context';
import { GET_COUNTRIES, GET_USER_LOGIN, type GetCountriesQuery, type GetUserLoginQuery } from '../../graphql/queries';
import { CustomFilters } from './CustomFilters';
import { ChartActionsRow, ChartScroll, ChartScrollInner, Grid, GridHeader, GridRow } from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { aggregateToChartSeries, type DateGranularity } from './helpers';
import { type UserFieldType } from './types';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';
import { ChartExportMenu, GranularitySelector, TableView } from './components';

export const UserLoginsStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);
  const { data: communityUserFields } = useUserFields();
  const [countries, setCountries] = useState<string[]>([]);
  const [endDate, setEndDate] = useState(() => toDate(new Date()));
  const [startDate, setStartDate] = useState(() => toDate(subWeeks(new Date(), 4)));
  const [skipQuery, setSkipQuery] = useState(false);
  const [userLoginData, setUserLoginData] = useState<GetUserLoginQuery>({ userLoginStats: [] });
  const [granularity, setGranularity] = useState<DateGranularity>('day');
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const userLoginsLabel = intl.formatMessage({ id: 'userLogins', defaultMessage: 'User Logins' });

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  const variables = {
    startDate,
    endDate,
    filters: { userFields: JSON.stringify(userFieldOptions) }
  };

  const { data: userLoginDataList, loading } = useQuery<GetUserLoginQuery>(GET_USER_LOGIN, {
    variables,
    skip: skipQuery,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  const chartSeriesData = useMemo(() => {
    return aggregateToChartSeries(userLoginData?.userLoginStats || [], granularity, intl);
  }, [userLoginData, granularity, intl]);

  const barChartData = useMemo(
    () => [
      {
        id: userLoginsLabel,
        data: chartSeriesData
      }
    ],
    [chartSeriesData, userLoginsLabel]
  );

  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeriesData.length), [chartSeriesData.length]);

  useEffect(() => {
    userLoginDataList && setUserLoginData(userLoginDataList);
  }, [userLoginDataList]);

  useEffect(() => {
    if (countriesList?.community) {
      countriesList.community && setCountries(countriesList.community.countries || []);
    }
  }, [countriesList]);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'date', defaultMessage: 'Date' }) },
      { label: intl.formatMessage({ id: 'users', defaultMessage: 'Users' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return (
      userLoginData?.userLoginStats
        ?.filter((item) => item.created)
        .map((item) => [new Date(item.created as number), item.total]) || []
    );
  }, [userLoginData?.userLoginStats]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const buildExportRows = useCallback(() => {
    return chartSeriesData.map((point) => [point.label, point.y]);
  }, [chartSeriesData]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildExportRows() };
  }, [buildExportRows, tableColumns]);

  if (loading) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  return (
    <Grid key="userLogins">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={setUserFieldOptions}
        setSkip={setSkipQuery}
      />
      <GridHeader key="dateRangeHeader">
        {intl.formatMessage({ id: 'dateRange', defaultMessage: 'Date range' })}
      </GridHeader>
      <GridRow key="dateRange">
        <DateRangePicker
          start={{
            label: intl.formatMessage({ id: 'start', defaultMessage: 'Start' }),
            date: startDate,
            onDateChange(date: Date | null) {
              setSkipQuery(true);
              setStartDate(date || toDate(subWeeks(new Date(), 4)));
            }
          }}
          end={{
            label: intl.formatMessage({ id: 'end', defaultMessage: 'End' }),
            date: endDate,
            onDateChange(date: Date | null) {
              setSkipQuery(true);
              setEndDate(date || toDate(new Date()));
            }
          }}
        />
      </GridRow>

      <GridRow key="updateButton">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Button
            priority="secondary"
            onClick={() => {
              setSkipQuery(false);
            }}
          >
            {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
          </Button>
          <GranularitySelector granularity={granularity} onChange={setGranularity} />
        </div>
      </GridRow>
      {!dataTableViewEnabled && (
        <ChartActionsRow key="userLoginsExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'userLogins', defaultMessage: 'User Logins' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart" data-testid="highchartUserLogins">
        {dataTableViewEnabled ? (
          <TableView
            columns={tableColumns}
            rows={tableRows}
            formatDate={formatDate}
            summary={intl.formatMessage({ id: 'userLogins', defaultMessage: 'User Logins' })}
          />
        ) : (
          <ChartScroll width={chartScrollWidth}>
            <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
              <ThemeProvider theme={accessibleChartTheme}>
                <BarChart
                  key={`chart-${granularity}`}
                  data={barChartData}
                  hasLegend={false}
                  hasValueLabels
                  axisX={{
                    formatTick: (value) => {
                      const index = Number(value);
                      if (!Number.isFinite(index) || !chartSeriesData[index]) {
                        return '';
                      }
                      return chartSeriesData[index].shortLabel;
                    },
                    formatTooltipValue: (value) => {
                      const index = Number(value);
                      return Number.isFinite(index) && chartSeriesData[index] ? chartSeriesData[index].label : value;
                    }
                  }}
                  axisY={{
                    minTickInterval: 1,
                    formatScreenReaderValue: (value) => {
                      return `${userLoginsLabel}: ${value}`;
                    }
                  }}
                />
              </ThemeProvider>
            </ChartScrollInner>
          </ChartScroll>
        )}
      </GridRow>
      {(userLoginData?.userLoginStats || []).length === 0 && (
        <GridRow key="message">
          <ErrorMessage
            message={intl.formatMessage({ id: 'noUsersFound', defaultMessage: 'No users found' })}
            details={intl.formatMessage({
              id: 'pleaseAdjustYourSearch',
              defaultMessage: 'Please adjust your search parameters and try again.'
            })}
          />
        </GridRow>
      )}
    </Grid>
  );
};
